import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, AlertTriangle, FileDown, ArrowLeftRight, Search, MapPin } from "lucide-react";
import { StockBadge } from "@/components/Badges";
import { Attachments } from "@/components/Attachments";
import { DatePicker } from "@/components/DatePicker";
import { exportToCsv } from "@/lib/exportCsv";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { pdfHeader, trAscii } from "@/lib/pdf";
import { toast } from "sonner";

const EMPTY = { code: "", name: "", unit: "Adet", category: "Yedek Parça", warehouse: "", initial_stock: 0, incoming: 0, outgoing: 0, min_stock: 0, unit_price: 0 };
const CATEGORIES = ["Yedek Parça", "Sarf Malzemesi", "Genel Malzeme"];

export default function Stock() {
  const { user } = useAuth();
  const canEdit = user?.role !== "viewer";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [mvOpen, setMvOpen] = useState(false);
  const [mvPart, setMvPart] = useState(null);
  const [movements, setMovements] = useState([]);
  const [mvType, setMvType] = useState("Çıkış");
  const [mvQty, setMvQty] = useState("");
  const [mvNote, setMvNote] = useState("");
  const [mvDest, setMvDest] = useState("");
  const [mvDate, setMvDate] = useState("");
  const [q, setQ] = useState("");
  const [whFilter, setWhFilter] = useState("all");
  const [catFilter, setCatFilter] = useState("all");
  const [locations, setLocations] = useState([]);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [bsOpen, setBsOpen] = useState(false);
  const [bsDest, setBsDest] = useState("");
  const [bsDate, setBsDate] = useState("");
  const [bsNote, setBsNote] = useState("");
  const [bsQty, setBsQty] = useState({});

  const warehouses = warehouseOptions;

  const openBulkSend = () => { setBsDest(""); setBsDate(new Date().toISOString().slice(0, 10)); setBsNote(""); setBsQty({}); setBsOpen(true); };
  const bsItems = () => Object.entries(bsQty).map(([part_id, v]) => ({ part_id, quantity: parseFloat(v) })).filter((x) => x.quantity > 0);
  const bulkSend = async (withPdf) => {
    if (!bsDest.trim()) { toast.error("Gönderilecek sahayı girin"); return; }
    const items = bsItems();
    if (items.length === 0) { toast.error("En az bir malzemeye miktar girin"); return; }
    try {
      const resp = await api.post("/stock/bulk-send", { destination: bsDest, date: bsDate, note: bsNote, items });
      toast.success(`${resp.data.count} malzeme "${bsDest}" sahasına gönderildi`);
      if (withPdf) bulkSendPdf(resp.data);
      setBsOpen(false); load(q);
    } catch (e) { toast.error(e?.response?.data?.detail || "İşlem başarısız"); }
  };
  const bulkSendPdf = (d) => {
    const t = trAscii;
    const doc = new jsPDF();
    let hy = pdfHeader(doc);
    doc.setFontSize(18); doc.setTextColor(0);
    doc.text(t("MALZEME TESLİM TUTANAĞI"), 105, hy + 8, { align: "center" });
    doc.setFontSize(11); doc.setTextColor(90);
    doc.text(t(`Saha: ${d.destination}`), 20, hy + 20);
    doc.text(t(`Tarih: ${d.date}`), 150, hy + 20);
    autoTable(doc, {
      startY: hy + 26,
      head: [["#", t("Malzeme"), t("Kod"), t("Miktar")]],
      body: d.items.map((it, i) => [i + 1, t(it.part_name || ""), t(it.part_code || ""), `${it.quantity} ${t(it.unit || "")}`]),
      styles: { font: "helvetica", fontSize: 10 },
      headStyles: { fillColor: [30, 41, 59] },
    });
    let y = doc.lastAutoTable.finalY + 24;
    if (y > 250) { doc.addPage(); y = 40; }
    doc.setTextColor(0); doc.setDrawColor(180);
    doc.text(t("Teslim Eden"), 40, y + 8, { align: "center" }); doc.line(20, y, 90, y);
    doc.text(t("Teslim Alan (Saha Şefi)"), 150, y + 8, { align: "center" }); doc.line(120, y, 190, y);
    doc.save(`toplu_teslim_${(d.destination || "saha").replace(/\s+/g, "_")}_${d.date}.pdf`);
  };

  const filtered = rows.filter((r) =>
    (whFilter === "all" || r.warehouse === whFilter) &&
    (catFilter === "all" || (r.category || "Yedek Parça") === catFilter)
  );

  const exportMovements = async () => {
    try {
      const res = await fetch(`${API}/stock/${mvPart.id}/movements/export`, { credentials: "include" });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stok_hareket_${mvPart.code}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Hareket geçmişi indirildi");
    } catch (e) { toast.error("İndirme başarısız"); }
  };

  const load = async (searchQ) => {
    setLoading(true);
    try {
      const res = await api.get("/stock", { params: { q: (searchQ ?? q) || undefined } });
      setRows(res.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(() => load(q), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { api.get("/locations-list").then((r) => setLocations(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/warehouses-list").then((r) => setWarehouseOptions(r.data)).catch(() => {}); }, [rows]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (r) => {
    setForm({ code: r.code, name: r.name, unit: r.unit, category: r.category || "Yedek Parça", warehouse: r.warehouse || "", initial_stock: r.initial_stock, incoming: r.incoming, outgoing: r.outgoing, min_stock: r.min_stock, unit_price: r.unit_price || 0 });
    setEditId(r.id); setOpen(true);
  };

  const num = (v) => parseFloat(v) || 0;
  const save = async () => {
    if (!form.code || !form.name) { toast.error("Kod ve parça adı zorunludur"); return; }
    const payload = { ...form, initial_stock: num(form.initial_stock), incoming: num(form.incoming), outgoing: num(form.outgoing), min_stock: num(form.min_stock), unit_price: num(form.unit_price) };
    try {
      if (editId) { await api.put(`/stock/${editId}`, payload); toast.success("Parça güncellendi"); }
      else { await api.post("/stock", payload); toast.success("Parça eklendi"); }
      setOpen(false); load(q);
    } catch (e) { toast.error("İşlem başarısız"); }
  };

  const remove = async (id) => { await api.delete(`/stock/${id}`); toast.success("Silindi"); load(q); };

  const openMovements = async (r) => {
    setMvPart(r); setMvOpen(true); setMovements([]); setMvType("Çıkış"); setMvQty(""); setMvNote(""); setMvDest(""); setMvDate(new Date().toISOString().slice(0, 10));
    try { const res = await api.get(`/stock/${r.id}/movements`); setMovements(res.data); } catch (e) {}
  };
  const addMovement = async () => {
    const qty = parseFloat(mvQty);
    if (isNaN(qty) || qty <= 0) { toast.error("Geçerli bir miktar girin"); return; }
    if (mvType !== "Giriş" && !mvDest.trim()) { toast.error(mvType === "İade" ? "İade edilen sahayı girin" : mvType === "Tüketim" ? "Tüketilen sahayı girin" : "Gönderildiği sahayı girin"); return; }
    try {
      const resp = await api.post(`/stock/${mvPart.id}/movements`, { type: mvType, quantity: qty, note: mvNote, destination: mvDest, date: mvDate });
      const msg = mvType === "İade" ? `İade alındı ← ${mvDest}` : mvType === "Tüketim" ? `Sahada kullanıldı: ${mvDest}` : mvType === "Çıkış" ? `Çıkış kaydedildi → ${mvDest}` : "Hareket kaydedildi";
      toast.success(msg);
      setMvPart(resp.data);
      const res = await api.get(`/stock/${mvPart.id}/movements`);
      setMovements(res.data);
      setMvQty(""); setMvNote(""); setMvDest("");
      load(q);
    } catch (e) { toast.error(e?.response?.data?.detail || "İşlem başarısız"); }
  };

  const exportWarehouse = async () => {
    try {
      const params = new URLSearchParams();
      if (whFilter !== "all") params.set("warehouse", whFilter);
      if (catFilter !== "all") params.set("category", catFilter);
      const res = await fetch(`${API}/export/stock?${params.toString()}`, { credentials: "include" });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "stok_raporu.xlsx";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(whFilter === "all" ? "Tüm stok raporu indirildi" : "Depo raporu indirildi");
    } catch (e) { toast.error("İndirme başarısız"); }
  };

  const exportCsv = () => {
    if (rows.length === 0) { toast.info("Aktarılacak veri yok"); return; }
    exportToCsv("stok_malzeme", [
      { key: "code", label: "Parça Kodu" },
      { key: "name", label: "Parça Adı" },
      { key: "category", label: "Kategori" },
      { key: "unit", label: "Birim" },
      { key: "warehouse", label: "Depo / Raf" },
      { key: "initial_stock", label: "Başlangıç Stoğu" },
      { key: "incoming", label: "Giren" },
      { key: "outgoing", label: "Çıkan" },
      { key: "remaining", label: "Kalan Stok" },
      { key: "min_stock", label: "Minimum Stok" },
      { key: "unit_price", label: "Birim Fiyat (TL)" },
      { key: "status", label: "Durum" },
    ], rows);
  };

  const criticalCount = rows.filter((r) => r.is_critical).length;

  return (
    <Layout
      title="Stok / Malzeme Takibi"
      subtitle="Yedek parça, sarf malzemesi ve genel malzeme · Kalan = Başlangıç + Giren − Çıkan"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          {canEdit && (
          <Button data-testid="bulk-send-btn" onClick={openBulkSend} variant="outline" className="rounded-sm gap-2 border-secondary text-secondary hover:bg-secondary/10">
            <MapPin className="w-4 h-4" /> Sahaya Toplu Gönder
          </Button>
          )}
          <Button data-testid="export-warehouse-btn" onClick={exportWarehouse} variant="outline" className="rounded-sm gap-2">
            <FileDown className="w-4 h-4" /> {whFilter === "all" ? "Depo Raporu" : "Bu Depoyu İndir"}
          </Button>
          {canEdit && (
          <Button data-testid="add-stock-btn" onClick={openNew} className="rounded-sm gap-2 bg-primary hover:bg-primary/90">
            <Plus className="w-4 h-4" /> Parça Ekle
          </Button>
          )}
        </div>
      }
    >
      {criticalCount > 0 && (
        <div data-testid="stock-critical-banner" className="mb-4 flex items-center gap-3 bg-red-50 border border-red-200 text-red-800 rounded-sm px-4 py-3">
          <AlertTriangle className="w-5 h-5" />
          <span className="text-sm font-semibold">{criticalCount} parça kritik stok seviyesinin altında!</span>
        </div>
      )}

      <div className="mb-4 flex flex-col sm:flex-row gap-3 max-w-4xl">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="stock-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Parça ara... (örn. akü)" className="pl-9 rounded-sm bg-white" />
        </div>
        <Select value={whFilter} onValueChange={setWhFilter}>
          <SelectTrigger data-testid="stock-warehouse-filter" className="rounded-sm bg-white sm:w-56"><SelectValue placeholder="Depo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Depolar</SelectItem>
            {warehouses.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger data-testid="stock-category-filter" className="rounded-sm bg-white sm:w-48"><SelectValue placeholder="Kategori" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Kategoriler</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Card className="rounded-sm border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50">
              <TableHead className="text-xs font-bold uppercase tracking-wide">Parça Kodu</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Parça Adı</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Kategori</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Birim</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Depo / Raf</TableHead>
              <TableHead className="text-right text-xs font-bold uppercase tracking-wide">Başlangıç</TableHead>
              <TableHead className="text-right text-xs font-bold uppercase tracking-wide">Giren</TableHead>
              <TableHead className="text-right text-xs font-bold uppercase tracking-wide">Çıkan</TableHead>
              <TableHead className="text-right text-xs font-bold uppercase tracking-wide">Kalan</TableHead>
              <TableHead className="text-right text-xs font-bold uppercase tracking-wide">Min.</TableHead>
              <TableHead className="text-right text-xs font-bold uppercase tracking-wide">Birim Fiyat</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wide">Durum</TableHead>
              <TableHead className="text-right text-xs font-bold uppercase tracking-wide">İşlem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={13} className="text-center py-10 text-muted-foreground">Yükleniyor...</TableCell></TableRow>}
            {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={13} className="text-center py-10 text-muted-foreground">Kayıt bulunamadı</TableCell></TableRow>}
            {filtered.map((r) => (
              <TableRow key={r.id} data-testid={`stock-row-${r.code}`} className={r.is_critical ? "bg-red-50/60" : "odd:bg-white even:bg-zinc-50/50"}>
                <TableCell className="font-mono font-semibold">{r.code}</TableCell>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell><CategoryBadge category={r.category || "Yedek Parça"} /></TableCell>
                <TableCell className="text-muted-foreground">{r.unit}</TableCell>
                <TableCell className="text-sm">{r.warehouse || "-"}</TableCell>
                <TableCell className="text-right font-mono">{r.initial_stock}</TableCell>
                <TableCell className="text-right font-mono text-emerald-700">+{r.incoming}</TableCell>
                <TableCell className="text-right font-mono text-red-700">−{r.outgoing}</TableCell>
                <TableCell className={`text-right font-mono font-bold ${r.is_critical ? "text-red-700" : "text-zinc-950"}`}>{r.remaining}</TableCell>
                <TableCell className="text-right font-mono text-muted-foreground">{r.min_stock}</TableCell>
                <TableCell className="text-right font-mono">{r.unit_price ? `${r.unit_price.toLocaleString("tr-TR")} ₺` : <span className="text-muted-foreground text-xs">—</span>}</TableCell>
                <TableCell><StockBadge critical={r.is_critical} /></TableCell>
                <TableCell className="text-right">
                  <Button data-testid={`movements-stock-${r.code}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-secondary" onClick={() => openMovements(r)} title="Hareket Geçmişi"><ArrowLeftRight className="w-4 h-4" /></Button>
                  {canEdit && <>
                  <Button data-testid={`edit-stock-${r.code}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm" onClick={() => openEdit(r)}><Pencil className="w-4 h-4" /></Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><Button data-testid={`delete-stock-${r.code}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-red-600"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                    <AlertDialogContent className="rounded-sm">
                      <AlertDialogHeader><AlertDialogTitle>Parça silinsin mi?</AlertDialogTitle><AlertDialogDescription>{r.name} ({r.code}) silinecek.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Vazgeç</AlertDialogCancel><AlertDialogAction className="rounded-sm bg-red-600 hover:bg-red-700" onClick={() => remove(r.id)}>Sil</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  </>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Parça Düzenle" : "Yeni Yedek Parça"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <Field label="Parça Kodu *"><Input data-testid="stk-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="rounded-sm" /></Field>
            <Field label="Parça Adı *"><Input data-testid="stk-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" /></Field>
            <Field label="Birim"><Input data-testid="stk-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="Adet / Kg / Litre / Çift" className="rounded-sm" /></Field>
            <Field label="Kategori">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="stk-category" className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Depo / Raf"><Input data-testid="stk-warehouse" value={form.warehouse} onChange={(e) => setForm({ ...form, warehouse: e.target.value })} placeholder="Örn. Merkez Depo - Raf A3" className="rounded-sm" /></Field>
            <Field label="Başlangıç Stoğu"><Input data-testid="stk-initial" type="number" value={form.initial_stock} onChange={(e) => setForm({ ...form, initial_stock: e.target.value })} className="rounded-sm" /></Field>
            <Field label="Giren"><Input data-testid="stk-incoming" type="number" value={form.incoming} onChange={(e) => setForm({ ...form, incoming: e.target.value })} className="rounded-sm" /></Field>
            <Field label="Çıkan"><Input data-testid="stk-outgoing" type="number" value={form.outgoing} onChange={(e) => setForm({ ...form, outgoing: e.target.value })} className="rounded-sm" /></Field>
            <Field label="Minimum Stok Sınırı"><Input data-testid="stk-min" type="number" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} className="rounded-sm" /></Field>
            <Field label="Birim Fiyat (₺) — maliyet için"><Input data-testid="stk-price" type="number" step="0.01" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} placeholder="0" className="rounded-sm" /></Field>
            <div className="flex items-end pb-2">
              <div className="text-sm">
                <span className="text-muted-foreground">Kalan: </span>
                <span className="font-mono font-bold">{num(form.initial_stock) + num(form.incoming) - num(form.outgoing)}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>İptal</Button>
            <Button data-testid="save-stock-btn" className="rounded-sm bg-primary hover:bg-primary/90" onClick={save}>Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={mvOpen} onOpenChange={setMvOpen}>
        <DialogContent className="rounded-sm w-[calc(100vw-1rem)] max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5 text-secondary" />
              Hareket Geçmişi — {mvPart?.name} ({mvPart?.code})
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 min-w-0">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <div className="text-sm">
                <span className="text-muted-foreground">Mevcut kalan: </span>
                <span className={`font-mono font-bold ${mvPart?.is_critical ? "text-red-700" : ""}`}>{mvPart?.remaining} {mvPart?.unit}</span>
                <span className="text-muted-foreground"> · Min: {mvPart?.min_stock}</span>
              </div>
              <Button data-testid="export-movements-btn" onClick={exportMovements} variant="outline" size="sm" className="rounded-sm gap-2">
                <FileDown className="w-3.5 h-3.5" /> Excel İndir
              </Button>
            </div>
            {(() => {
              const qn = parseFloat(mvQty);
              if (mvType === "Çıkış" && !isNaN(qn) && qn > 0 && mvPart) {
                const after = (mvPart.remaining || 0) - qn;
                if (after < 0) return (
                  <div data-testid="stock-negative-warn" className="mb-3 flex items-center gap-2 bg-red-50 border border-red-200 text-red-800 rounded-sm px-3 py-2 text-sm">
                    <AlertTriangle className="w-4 h-4" /> Yetersiz stok! Bu çıkış sonrası kalan {after} {mvPart.unit} olur. İşlem reddedilecek.
                  </div>
                );
                if (after <= (mvPart.min_stock || 0)) return (
                  <div data-testid="stock-low-warn" className="mb-3 flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-sm px-3 py-2 text-sm">
                    <AlertTriangle className="w-4 h-4" /> Dikkat: Bu çıkış sonrası kalan {after} {mvPart.unit} → minimum ({mvPart.min_stock}) seviyesinde/altında.
                  </div>
                );
              }
              return null;
            })()}
            {canEdit && (
            <div className="border-b border-border pb-4 mb-3 space-y-2">
              <div className="flex flex-wrap items-end gap-2">
                <div className="w-full sm:w-32">
                  <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Tip</Label>
                  <Select value={mvType} onValueChange={setMvType}>
                    <SelectTrigger data-testid="mv-type" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Giriş">Giriş</SelectItem>
                      <SelectItem value="Çıkış">Çıkış</SelectItem>
                      <SelectItem value="İade">İade (Sahadan Dönen)</SelectItem>
                      <SelectItem value="Tüketim">Tüketim (Sahada Kullanıldı)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-24">
                  <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Miktar</Label>
                  <Input data-testid="mv-qty" type="number" value={mvQty} onChange={(e) => setMvQty(e.target.value)} className="rounded-sm mt-1" />
                </div>
                <div className="flex-1 min-w-[120px]">
                  <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Not</Label>
                  <Input data-testid="mv-note" value={mvNote} onChange={(e) => setMvNote(e.target.value)} placeholder="Opsiyonel" className="rounded-sm mt-1" />
                </div>
                {mvType === "Giriş" && <Button data-testid="add-movement-btn" onClick={addMovement} className="rounded-sm bg-primary hover:bg-primary/90 w-full sm:w-auto">Ekle</Button>}
              </div>
              {mvType !== "Giriş" && (
                <div className="flex flex-wrap items-end gap-2">
                  <div className="flex-1 min-w-[160px]">
                    <Label className="text-xs font-bold uppercase tracking-wide text-secondary">{mvType === "İade" ? "İade Edilen Saha *" : mvType === "Tüketim" ? "Tüketilen Saha *" : "Gönderildiği Saha *"}</Label>
                    <Input data-testid="mv-destination" list="mv-dest-options" value={mvDest} onChange={(e) => setMvDest(e.target.value)} placeholder="Örn. Kocaeli Şantiyesi" className="rounded-sm mt-1" />
                    <datalist id="mv-dest-options">
                      {locations.map((l) => <option key={l} value={l} />)}
                    </datalist>
                  </div>
                  <div className="w-full sm:w-40">
                    <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{mvType === "İade" ? "İade Tarihi" : mvType === "Tüketim" ? "Tüketim Tarihi" : "Gönderim Tarihi"}</Label>
                    <div className="mt-1"><DatePicker testid="mv-date" value={mvDate} onChange={setMvDate} /></div>
                  </div>
                  <Button data-testid="add-movement-btn" onClick={addMovement} className="rounded-sm bg-primary hover:bg-primary/90 w-full sm:w-auto">{mvType === "İade" ? "İade Al" : mvType === "Tüketim" ? "Düş" : "Gönder"}</Button>
                </div>
              )}
            </div>
            )}
            <div className="max-h-[280px] overflow-y-auto overflow-x-auto min-w-0">
              {movements.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">Henüz hareket kaydı yok</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-zinc-50">
                      <TableHead className="text-xs font-bold uppercase tracking-wide">Tarih</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wide">Tip</TableHead>
                      <TableHead className="text-right text-xs font-bold uppercase tracking-wide">Miktar</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wide">Gönderildiği Saha</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wide">Not</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wide">Kullanıcı</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {movements.map((mv) => (
                      <TableRow key={mv.id} data-testid={`movement-row-${mv.id}`} className="odd:bg-white even:bg-zinc-50/50">
                        <TableCell className="font-mono text-xs whitespace-nowrap">
                          {mv.date ? mv.date.split("-").reverse().join(".") : (mv.timestamp || "").slice(0, 10).split("-").reverse().join(".")}
                          {mv.timestamp && <span className="block text-[10px] text-muted-foreground">{new Date(mv.timestamp).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}</span>}
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center rounded-sm px-2 py-0.5 text-xs font-bold ${mv.type === "Giriş" ? "bg-emerald-100 text-emerald-800" : mv.type === "İade" ? "bg-blue-100 text-blue-800" : mv.type === "Tüketim" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}`}>{mv.type}</span>
                        </TableCell>
                        <TableCell className={`text-right font-mono font-semibold ${mv.type === "Çıkış" ? "text-red-700" : mv.type === "Tüketim" ? "text-amber-700" : "text-emerald-700"}`}>{mv.type === "Çıkış" ? "−" : mv.type === "Tüketim" ? "" : "+"}{mv.quantity}</TableCell>
                        <TableCell className="text-sm">{mv.destination ? <span className="inline-flex items-center gap-1 text-secondary font-medium"><MapPin className="w-3 h-3" />{mv.destination}</span> : <span className="text-muted-foreground">-</span>}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{mv.note || "-"}</TableCell>
                        <TableCell className="text-sm">{mv.actor_name || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
            <div className="border-t border-border pt-4 mt-4">
              <Attachments entityType="stock" entityId={mvPart?.id} />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={bsOpen} onOpenChange={setBsOpen}>
        <DialogContent className="rounded-sm max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <MapPin className="w-5 h-5 text-secondary" /> Sahaya Toplu Gönderim
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5 sm:col-span-1">
                <Label className="text-xs font-bold uppercase tracking-wide text-secondary">Gönderilecek Saha *</Label>
                <Input data-testid="bs-destination" list="bs-dest-options" value={bsDest} onChange={(e) => setBsDest(e.target.value)} placeholder="Örn. Kocaeli Şantiyesi" className="rounded-sm" />
                <datalist id="bs-dest-options">{locations.map((l) => <option key={l} value={l} />)}</datalist>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Tarih</Label>
                <DatePicker testid="bs-date" value={bsDate} onChange={setBsDate} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Not</Label>
                <Input data-testid="bs-note" value={bsNote} onChange={(e) => setBsNote(e.target.value)} placeholder="Opsiyonel" className="rounded-sm" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Malzeme ve Miktar</p>
                <span className="text-xs text-muted-foreground" data-testid="bs-selected-count">{bsItems().length} malzeme seçili</span>
              </div>
              <div className="border border-border rounded-sm max-h-[320px] overflow-y-auto divide-y divide-border">
                {rows.length === 0 && <p className="text-center py-6 text-muted-foreground text-sm">Malzeme yok</p>}
                {rows.map((r) => (
                  <div key={r.id} data-testid={`bs-item-${r.code}`} className="flex items-center gap-3 px-3 py-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{r.name} <span className="font-mono text-xs text-muted-foreground">({r.code})</span></p>
                      <p className="text-xs text-muted-foreground">Kalan: <span className="font-mono">{r.remaining} {r.unit}</span> · {r.warehouse || "-"}</p>
                    </div>
                    <Input data-testid={`bs-qty-${r.code}`} type="number" min="0" max={r.remaining} value={bsQty[r.id] || ""} onChange={(e) => setBsQty({ ...bsQty, [r.id]: e.target.value })} placeholder="0" className="rounded-sm w-24 h-9" />
                    <span className="text-xs text-muted-foreground w-10 shrink-0">{r.unit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setBsOpen(false)}>İptal</Button>
            <Button data-testid="bs-send-pdf" variant="outline" className="rounded-sm gap-2" onClick={() => bulkSend(true)}>
              <FileDown className="w-4 h-4" /> Gönder + Tutanak
            </Button>
            <Button data-testid="bs-send-submit" className="rounded-sm bg-primary hover:bg-primary/90" onClick={() => bulkSend(false)}>
              Gönder ({bsItems().length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function Field({ label, children }) {
  return (<div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{label}</Label>{children}</div>);
}

function CategoryBadge({ category }) {
  const styles = {
    "Yedek Parça": "bg-blue-100 text-blue-800",
    "Sarf Malzemesi": "bg-amber-100 text-amber-800",
    "Genel Malzeme": "bg-emerald-100 text-emerald-800",
  };
  return (
    <span className={`inline-flex items-center rounded-sm px-2 py-0.5 text-xs font-semibold whitespace-nowrap ${styles[category] || "bg-zinc-100 text-zinc-700"}`}>
      {category}
    </span>
  );
}
